import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Alert, Container, Row } from "reactstrap";

import BreadCrumb from "Components/Common/BreadCrumb";
import { usePageActivity } from "Components/Hooks/UsePageActivity";
import { getActivityReport } from "common/services/analytics.service";

import ReportFilters, { DEFAULT_FILTERS } from "./ReportFilters";
import SummaryTiles from "./SummaryTiles";
import UsageTrend from "./UsageTrend";
import AdoptionAndStickiness from "./AdoptionAndStickiness";
import WhenUsed from "./WhenUsed";
import AgencyEngagement from "./AgencyEngagement";
import MostActiveUsers from "./MostActiveUsers";
import TopPages from "./TopPages";
import MostViewedRecords from "./MostViewedRecords";
import {
  ActivityReportData,
  ActivityReportMeta,
  ReportFilterValue,
} from "./types";

/** Dates are compared as plain UTC days, so a range means whole days. */
const toApiDate = (date: Date | null) =>
  date ? new Date(date).toLocaleDateString("en-CA") : undefined;

/**
 * The Activity Report is served by a single endpoint: one fetch per filter
 * change feeds every widget below, so the whole page always shows one
 * consistent snapshot rather than each card carrying its own period.
 */
const ActivityReport = () => {
  const location = useLocation();
  usePageActivity(location.pathname, "Activity Report");

  const [filters, setFilters] = useState<ReportFilterValue>({
    ...DEFAULT_FILTERS,
  });
  const [data, setData] = useState<ActivityReportData | null>(null);
  const [meta, setMeta] = useState<ActivityReportMeta | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    let cancelled = false;

    const fetchReport = async () => {
      setLoading(true);
      setError("");

      const usingCustomRange = Boolean(filters.fromDate && filters.toDate);

      try {
        const response: any = await getActivityReport({
          period: filters.period,
          ...(usingCustomRange
            ? {
                startDate: toApiDate(filters.fromDate),
                endDate: toApiDate(filters.toDate),
              }
            : {}),
          ...(filters.agency ? { agency: filters.agency } : {}),
          ...(filters.page ? { page: filters.page } : {}),
          ...(filters.user ? { user: filters.user } : {}),
          limit: 10,
        });

        if (cancelled) return;

        if (!response?.data) {
          setData(null);
          setMeta(null);
          setError("The activity report could not be loaded.");
          return;
        }

        setData(response.data);
        setMeta(response.meta || null);
      } catch (e) {
        if (cancelled) return;
        setData(null);
        setMeta(null);
        setError("The activity report could not be loaded.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchReport();

    return () => {
      cancelled = true;
    };
  }, [filters]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Activity Report"
            pageTitles={[
              { title: "Dashboard", link: "/dashboard" },
              { title: "User Activity", link: "#" },
            ]}
          />

          <ReportFilters
            value={filters}
            onChange={setFilters}
            meta={meta}
            loading={loading}
          />

          {error ? (
            <Alert color="danger" fade={false} className="mb-4">
              {error}
            </Alert>
          ) : null}

          <Row>
            <SummaryTiles
              data={data}
              comparable={Boolean(meta?.comparable)}
              loading={loading}
            />
          </Row>

          <Row>
            <UsageTrend data={data} loading={loading} />
            <AdoptionAndStickiness data={data} />
          </Row>

          <Row>
            <WhenUsed data={data} loading={loading} />
          </Row>

          <Row>
            <AgencyEngagement data={data} loading={loading} />
            <MostActiveUsers data={data} loading={loading} />
          </Row>

          <Row>
            <TopPages data={data} loading={loading} />
            <MostViewedRecords data={data} loading={loading} />
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ActivityReport;
